import type {
  CreateCommentRequest,
  CreateTaskRequest,
  MyTasksPage,
  TaskSummary,
  UpdateTaskRequest,
} from '@ofmos/shared/annotations';
import type { AuthenticatedUser, UserPreferences } from '@ofmos/shared/auth';
import type { FilterNode } from '@ofmos/shared/filter-ast';
import type { HealthConfig } from '@ofmos/shared/health-config';
import { newId } from '@ofmos/shared/ids';
import type { IntakeSourcePatchInput, IntakeSourceWriteInput } from '@ofmos/shared/intake';
import type { AggregateRequest, GroupDimension } from '@ofmos/shared/record-group';
import type { OrderBy } from '@ofmos/shared/record-query';
import type { ReportPatchInput, ReportRange, ReportWriteInput } from '@ofmos/shared/report';
import type { CreateRoleRequest, UpdateRoleRequest } from '@ofmos/shared/roles';
import type {
  CreateWebhookSubscriptionRequest,
  UpdateWebhookSubscriptionRequest,
  WebhookDeliveryStatus,
  WebhookReplayMode,
} from '@ofmos/shared/webhook';
import {
  type InfiniteData,
  type QueryClient,
  replaceEqualDeep,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useAppClient } from './ClientProvider';
import {
  type AppClient,
  type CreateFieldInput,
  type CreateNamespaceInput,
  type CreateObjectInput,
  type DestructiveApplyOptions,
  type DropTarget,
  type FieldValue,
  type IntakeValidateInput,
  type JunctionLinkInput,
  type MyTaskCountFilter,
  type MyTasksFilter,
  type PendingChangeSummary,
  RECORD_EVENT_PAGE_SIZE,
  type RecordGroupSet,
  type RecordRow,
  type SavedViewPatchInput,
  type SavedViewWriteInput,
  type ThreadComment,
  type UpdateFieldInput,
  type UpdateNamespaceInput,
  type UpdateObjectInput,
} from './client';
import { ApiError } from './http/transport';
import { invalidateRecordReads } from './live/invalidation';
import { reconcileFilteredLists } from './optimisticList';
import {
  queryKeyPrefixes,
  queryKeys,
  type RecordGroupSignature,
  type RecordListSignature,
} from './queryKeys';

/**
 * The hooks every screen uses. They enforce INV-13 so no screen has to remember
 * it: list queries store ID ARRAYS, records live under the canonical key, and
 * mutations write that key optimistically.
 */

export function useObjects() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.objects(),
    queryFn: () => client.metadata.listObjects(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useObject(objectId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.object(objectId),
    queryFn: () => client.metadata.getObject(objectId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * `enabled` because a caller can hold an object id that is not there yet - a panel whose
 * object has not resolved, a section waiting for an endpoint. Without it those callers
 * pass `''` and the app issues a request for an object that cannot exist, which is a 404
 * in the console on every cold load of the dashboard.
 */
export function useFields(objectId: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.fields(objectId),
    queryFn: () => client.metadata.listFields(objectId),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: enabled && objectId !== '',
  });
}

/**
 * THE ADMIN SCREENS' OWN QUERIES (admin-area.md D8, D9, D10).
 *
 * Hidden entities, junction objects and archived rows, flagged rather than dropped -
 * which is what lets one screen offer the restore and the promotion. Separate hooks
 * rather than a parameter on `useObjects`, because the visibility must be a DELIBERATE
 * call at the call site: an accidental default here puts every junction table in
 * everybody's sidebar (D10's coupling note, and open risk 4).
 *
 * Nothing outside `app/admin/` may call these two.
 */
export function useAdminObjects() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.objects('all'),
    queryFn: () => client.metadata.listObjects('all'),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAdminObject(objectId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.object(objectId, 'all'),
    queryFn: () => client.metadata.getObject(objectId, 'all'),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAdminFields(objectId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.fields(objectId, 'all'),
    queryFn: () => client.metadata.listFields(objectId, 'all'),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export interface RecordListOptions {
  readonly select: readonly string[];
  /** The shared filter AST (INV-6), sent verbatim to `POST /api/query`. */
  readonly filter?: FilterNode | undefined;
  readonly orderBy?: readonly OrderBy[] | undefined;
  readonly limit?: number | undefined;
  readonly enabled?: boolean;
  /**
   * D21's Archived segment: tombstones INSTEAD OF live rows. It composes with `filter`,
   * so the archived view keeps whatever the user narrowed to.
   */
  readonly archivedOnly?: boolean;
  /**
   * D20: the size of the whole filtered set, for a header that says "Calls (11)".
   *
   * OFF by default. It is a second, unbounded scan of the same filter, so eight open
   * related sections would be sixteen statements rather than eight (open risk 3) - the
   * caller that shows a number pays for it and nobody else does.
   */
  readonly withTotalCount?: boolean;
}

export interface RecordListResult {
  readonly ids: readonly string[];
  readonly nextCursor?: string;
  /** Present exactly when the caller asked for it. */
  readonly totalCount?: number;
}

/**
 * A page of records. The rows land in their canonical cache entries; this query
 * keeps only their ids, which is what makes an inline edit re-render one cell
 * instead of the whole table.
 */
export function useRecordList(objectId: string, options: RecordListOptions) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  const signature = listSignature(options);

  return useQuery<RecordListResult>({
    queryKey: queryKeys.recordList(objectId, signature),
    enabled: options.enabled ?? true,
    queryFn: () => fetchListPage(client, queryClient, objectId, options),
  });
}

/**
 * THE PAGED LIST (D20), on the pattern `useRecordEvents` and `useMyTasks` already use.
 *
 * `useRecordList` is a single page and its `nextCursor` had exactly one consumer, which
 * used it as a truncation SIGNAL and never paged with it. A board column with a count
 * (D21) and a related section's "See all 11" both need the other half.
 *
 * THE CURSOR ENCODES THE SORT (`query-plan.ts`), and `decodeQueryCursor` answers 400 on a
 * mismatch - so a surface that changes `orderBy` must land on a DIFFERENT cache entry
 * rather than page an old cursor into a new sort. It does: `orderBy` is part of the
 * signature, and the signature is the key.
 *
 * AND A REFUSED PAGE IS NOT RETRIED (`table-completion.md` D26): see `retryRecordPage`.
 */
export function useInfiniteRecordList(objectId: string, options: RecordListOptions) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  const signature = listSignature(options);

  return useInfiniteQuery({
    queryKey: queryKeys.recordListPages(objectId, signature),
    enabled: options.enabled ?? true,
    queryFn: ({ pageParam }) =>
      fetchListPage(client, queryClient, objectId, options, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: RecordListResult) => last.nextCursor,
    retry: retryRecordPage,
  });
}

/**
 * `@tanstack/query-core`'s own default, mirrored because this predicate REPLACES it and must
 * not narrow it: `config.retry ?? 3`, retried while `failureCount < retry`
 * (`query-core/retryer.js`). A caller that refuses one class of error must keep the library's
 * behaviour for every other class, or a flaky network becomes a red screen.
 */
const DEFAULT_QUERY_RETRIES = 3;

/**
 * A PERMANENT ANSWER IS NOT RETRIED (`table-completion.md` D26).
 *
 * THE SCENE. A reader has walked five pages. A deploy lands, or any cursor goes stale, and
 * they press `Show 50 more`. Without this, the button spins through three retries of a 400
 * that cannot succeed - three round trips spent making a wrong screen arrive later - and then
 * the 250 rows they were reading are replaced by a red paragraph, because query-core's error
 * action sets `status: "error"` UNCONDITIONALLY even when the entry holds data. There is no
 * background-error arm that preserves `success`, which is why the status row and not the table
 * is where a failed APPEND belongs (D8's shape 5) and why the table's error arm is reserved
 * for a table with no rows at all.
 *
 * A 4xx IS THE SERVER'S FINAL WORD, and `ApiError` carries the status and the machine-readable
 * code the transport promises ("not a string a screen has to regex"). The cursor refusal's own
 * code is `invalid_state`, which is what turns shape 5 into a RESTART control rather than a
 * `Try again` that would answer 400 forever - see `resetRecordListPages`.
 */
export function retryRecordPage(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < DEFAULT_QUERY_RETRIES;
}

/**
 * START THE WALK AGAIN FROM THE FIRST ROWS (`table-completion.md` D26).
 *
 * The control D15's price assumed existed: a stale cursor cannot be retried, so the only way
 * forward is to drop the walk and fetch page one again. `resetQueries` is what does that -
 * it resets each matching entry and refetches the mounted ones - and the key is built HERE
 * from the same `listSignature` the hook uses, because a caller that assembled the key itself
 * would be the second place a page size or an archived segment reaches the cache.
 *
 * A FUNCTION AND NOT A MEMBER ON THE HOOK'S RESULT. `useInfiniteQuery`'s result is a tracked
 * proxy: spreading it to attach one more member reads every property and turns every field
 * into a re-render trigger for the four surfaces that already mount this hook. The caller
 * takes a `QueryClient` from `useQueryClient`, exactly as `invalidateRecordReads`' callers do.
 */
export function resetRecordListPages(
  queryClient: QueryClient,
  objectId: string,
  options: RecordListOptions,
): void {
  void queryClient.resetQueries({
    queryKey: queryKeys.recordListPages(objectId, listSignature(options)),
  });
}

/**
 * The count-only read's options: the LIST's options, minus the TWO members it does not get to
 * choose, so that a caller can pass `{ ...listOptions, select: [labelFieldId] }` and nothing
 * else. Deriving the sibling from the list rather than enumerating its members by hand is what
 * keeps `archivedOnly` travelling: enumerate them and the Archived segment shows the LIVE total
 * under archived rows, which is a wrong NUMBER on a screen rather than a shortened list.
 * TypeScript does not excess-property-check a spread, so the two omitted members can be spread
 * in harmlessly and are overridden below.
 *
 * `withTotalCount` is what this hook is FOR, and `limit` is one row by definition. Both being
 * the hook's rather than the caller's buys two things beyond tidiness: the identifier
 * `withTotalCount` never appears at a call site under `app/records`, so obligation 12's sweep
 * keeps meaning what it says; and no call site there writes a numeric `limit` literal, which is
 * what obligation 3 forbids and what the document's own D5 example would have done.
 */
export type RecordCountOptions = Omit<RecordListOptions, 'limit' | 'withTotalCount'>;

/**
 * ONE ROW, because `recordQuerySchema` has no "no rows" and the smallest legal page is the
 * cheapest way to ask a question about the WHOLE filtered set. It is the same number
 * `dashboard/queries.ts`' `countOnlyOptions` sends, and for the same reason: "a total and no
 * rows worth reading".
 */
const COUNT_ONLY_LIMIT = 1;

/**
 * THE SIZE OF THE WHOLE FILTERED SET. No rows: this hook writes nothing into the canonical
 * record entries, because a one-field read would SHRINK them (`table-completion.md` D5).
 *
 * WHY IT IS ITS OWN HOOK AND NOT `useRecordList` WITH `limit: 1`. `fetchListPage` ends in a
 * wholesale fan-out - `setQueryData(queryKeys.record(...), record)`, a WRITE and not a merge -
 * and `QueryRecord.values` is built from the SELECTED fields only. So a count-only read with
 * a one-field select returns one record, the first row under the default order, carrying only
 * the label field, and writes it over that record's canonical entry: row 1 of the table
 * renders empty in every column but its title, its record page inherits the blanked entry,
 * and it happens again on every frame that refetches both entries. Intermittent, which is the
 * worst shape a bug report can have. `dashboard/queries.ts` gives `rowsOptions` and
 * `countOnlyOptions` the same `select`, which keeps its own two reads consistent with each other
 * but does not stop the count-only one from writing a record entry - it goes through
 * `useRecordList` like any page. Here the count's select has to stay NARROW to keep the entry
 * stable, so the only safe arrangement is a read with no fan-out at all.
 *
 * WHY NOT REUSE THE LIST'S `select`. It would make the fan-out harmless and tie the count
 * entry's key to the visible columns, so hiding a column would cost a full scan of the
 * filter. One field id - the object's label identifier - keeps the entry STABLE under every
 * column toggle, and `recordQuerySchema` requires a non-empty `select` while `planRecordQuery`
 * runs `requireFieldAccess` over every entry, so a counting caller must name a field it may
 * read anyway.
 *
 * NO NEW KEY AND NO NEW PREFIX. It is an ordinary `queryKeys.recordList` entry whose signature
 * differs from the list's in three members - `select`, `limit` and `withTotalCount`, all
 * already members of `RecordListSignature` - so `['recordList', objectId]` reaches it and both
 * SSE paths and `invalidateRecordReads` cover it unchanged. `{ids: [], totalCount}` is a shape
 * `optimisticList.ts` already handles: it is the single-page `ListPage`, `publishesCount` reads
 * `first.totalCount`, and an empty `ids` array makes that file's `listed` false for every
 * record - which is why the reconciler leaves this entry alone when a row LEAVES the filter,
 * and why the live stream rather than the optimistic path is what repairs the number
 * (obligation 15, honesty register 14).
 */
export function useRecordCount(objectId: string, options: RecordCountOptions) {
  const client = useAppClient();
  /**
   * The two members after the spread, not before it: a caller who spread the list's options in
   * carries its page size at RUNTIME even though the type hides it, and a count that read fifty
   * rows to answer one number would be the mistake this hook exists to avoid.
   */
  const countOptions: RecordListOptions = {
    ...options,
    limit: COUNT_ONLY_LIMIT,
    withTotalCount: true,
  };
  const signature = listSignature(countOptions);

  return useQuery<RecordListResult>({
    queryKey: queryKeys.recordList(objectId, signature),
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const page = await client.records.query(recordQueryOf(objectId, countOptions), {
        archivedOnly: options.archivedOnly === true,
        withTotalCount: true,
      });
      // No ids and no cursor: this entry is not a walk, and it holds no row it could shrink.
      return {
        ids: [],
        ...(page.totalCount !== undefined ? { totalCount: page.totalCount } : {}),
      };
    },
  });
}

/**
 * THE GROUPED READ (group-by-aggregation.md D2, D21): one statement for every group and
 * every aggregate, under one repeatable-read snapshot, in ONE cache entry.
 *
 * It replaces the shape a caller would otherwise reach for - one counted query per value -
 * and the board is the first surface to spend that: N options used to be N `withTotalCount`
 * queries, each a second full scan of the filter, and it is now one statement (D23).
 *
 * The seeded rows land in their canonical entries exactly as `fetchListPage` writes them
 * (INV-13), so a card resolved from `['record', objectId, recordId]` renders whether a
 * page or a group brought it back.
 *
 * A grouped entry is only ever INVALIDATED, never patched: `reconcileFilteredLists` walks
 * the `recordList` prefix and cannot see this one, so no patching rule reaches it by
 * construction, and every write path invalidates it through `invalidateRecordReads` or -
 * for the one write that does not - through the reconciler itself (D25).
 */
export interface RecordGroupsOptions {
  readonly select: readonly string[];
  readonly filter?: FilterNode | undefined;
  readonly orderBy?: readonly OrderBy[] | undefined;
  readonly groupBy: readonly GroupDimension[];
  readonly aggregates?: readonly AggregateRequest[] | undefined;
  /**
   * How many groups at most (D20). EXPLICIT at every caller that renders a fixed column
   * or section per group: the default is 50, and a surface that draws one column per
   * option and asks for fewer counts than it has columns renders a column with no number.
   */
  readonly groupLimit?: number | undefined;
  /** 0 means aggregates only. A preview size, not a first page (D19). */
  readonly rowsPerGroup?: number | undefined;
  /**
   * D9's ARCHIVED SEGMENT, in the KEY as well as in the request, for the reason
   * `RecordListSignature.archivedOnly` is: Active and Archived are two lists over one
   * object with the same select, sort and grouping, so without it they would share ONE
   * cache entry and switching the segment would paint the live groups under the Archived
   * heading until the refetch landed. The condition that narrows the query is built inside
   * the client implementation, so it is invisible to `filter` and has to be keyed on here.
   */
  readonly archivedOnly?: boolean | undefined;
  readonly enabled?: boolean;
}

export function useRecordGroups(objectId: string, options: RecordGroupsOptions) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  const signature = groupSignature(options);

  return useQuery<RecordGroupSet>({
    queryKey: queryKeys.recordGroups(objectId, signature),
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const result = await client.records.queryGroups(
        {
          query: {
            objectId,
            select: [...options.select],
            ...(options.filter !== undefined ? { filter: options.filter } : {}),
            ...(options.orderBy !== undefined ? { orderBy: options.orderBy } : {}),
          },
          groupBy: options.groupBy,
          ...(options.aggregates === undefined ? {} : { aggregates: options.aggregates }),
          ...(options.groupLimit === undefined ? {} : { groupLimit: options.groupLimit }),
          ...(options.rowsPerGroup === undefined ? {} : { rowsPerGroup: options.rowsPerGroup }),
        },
        options.archivedOnly === undefined ? {} : { archivedOnly: options.archivedOnly },
      );
      // INV-13: a grouped response writes its records into the canonical keys too.
      for (const group of result.groups) {
        for (const record of group.records) {
          seedCanonicalRecord(queryClient, objectId, record);
        }
      }
      return result;
    },
  });
}

/** The key's payload, assembled in ONE place so the two hooks cannot disagree (INV-13). */
function listSignature(options: RecordListOptions): RecordListSignature {
  return {
    select: options.select,
    filter: options.filter,
    orderBy: options.orderBy,
    limit: options.limit,
    archivedOnly: options.archivedOnly,
    withTotalCount: options.withTotalCount,
  };
}

/**
 * The grouped key's payload (D25). The embedded query's signature plus exactly the
 * members of `RecordGroupQuery`, so nothing reaches the server without passing through
 * here - which is `RecordListSignature`'s own rule, applied one level up.
 */
function groupSignature(options: RecordGroupsOptions): RecordGroupSignature {
  return {
    query: {
      select: options.select,
      filter: options.filter,
      orderBy: options.orderBy,
      archivedOnly: options.archivedOnly,
    },
    groupBy: options.groupBy,
    aggregates: options.aggregates,
    groupLimit: options.groupLimit,
    rowsPerGroup: options.rowsPerGroup,
  };
}

/**
 * THE WIRE QUERY, ASSEMBLED IN ONE PLACE, so a count cannot drift from the page it counts
 * (`table-completion.md` D5).
 *
 * `useRecordCount` sends the list's own members verbatim with three replaced, and the reason
 * that is a mechanism rather than tidiness is `archivedOnly`'s sibling problem one layer up:
 * a member enumerated by hand in two places is a member one of them silently loses. What
 * this function deliberately does NOT carry is the second argument - `archivedOnly` and
 * `withTotalCount` are per-caller decisions and each read states its own.
 */
function recordQueryOf(objectId: string, options: RecordListOptions, cursor?: string) {
  return {
    objectId,
    select: [...options.select],
    ...(options.filter !== undefined ? { filter: options.filter } : {}),
    ...(options.orderBy !== undefined ? { orderBy: options.orderBy } : {}),
    ...(options.limit !== undefined ? { limit: options.limit } : {}),
    ...(cursor === undefined ? {} : { cursor }),
  };
}

/**
 * SEEDING A CANONICAL RECORD ENTRY FROM A LIST OR A GROUP (INV-13), AND WHY IT MERGES.
 *
 * OWNER CLICK-THROUGH FINDING C1 (wave 1b, DATA LOSS). `QueryRecord.values` is built from
 * the SELECTED fields only, and this fan-out used to be a wholesale `setQueryData` - so a
 * list entry whose `select` was assembled BEFORE a field existed keeps refetching with that
 * old select (its select is part of its cache key, and `recordInvalidations` refetches it at
 * `all`, unmounted included) and writes a record with NO KEY for the newer field over the
 * canonical entry. Every reader of that entry then sees `undefined`, which every field type
 * renders as empty.
 *
 * On a FILES cell that is not a cosmetic problem. The attachment list is a live control that
 * PATCHes the whole cell, so the next upload builds its array on the blank, sends it as the
 * complete value, and the server - correctly, per `file-attachments.md` D7 - unlinks every
 * file missing from it. The owner lost a photo that way: server value `[photo]`, screen "No
 * files yet", next upload `[txt]`, photo gone.
 *
 * `useRecordCount` already answers this hazard by fanning out NOTHING (see its docblock,
 * which describes exactly this failure and calls it "intermittent, which is the worst shape
 * a bug report can have"). A list that returns real rows cannot take that answer, so it
 * takes the other one: the fresh values WIN, and a field this query did not ask for KEEPS
 * the value the entry already had instead of losing it. A stale value for an unselected
 * field is repaired by the next full read; `undefined` is indistinguishable from empty and
 * is what a user acts on.
 *
 * The system half - `createdAt`, `updatedBy`, `provenance`, `deletedAt` - is NOT merged and
 * must not be: those ids are folded into every query's select by the client
 * (`recordsClient.systemFields`), so the row always carries them and they are always fresh.
 */
function seedCanonicalRecord(queryClient: QueryClient, objectId: string, record: RecordRow): void {
  let wasFull = false;
  const stored = queryClient.setQueryData<RecordRow>(
    queryKeys.record(objectId, record.id),
    (previous) => {
      wasFull = isFullRecord(previous);
      return previous === undefined
        ? record
        : { ...record, values: { ...previous.values, ...record.values } };
    },
  );
  // A merge keeps every value a full read brought, so the entry stays full (`isFullRecord`).
  if (wasFull && stored !== undefined) fullRecords.add(stored);
}

/**
 * One page, fanned into the canonical entries. Shared by both hooks, so "a list holds
 * ids and records live under `['record', objectId, recordId]`" is implemented once.
 */
async function fetchListPage(
  client: AppClient,
  queryClient: QueryClient,
  objectId: string,
  options: RecordListOptions,
  cursor?: string,
): Promise<RecordListResult> {
  const page = await client.records.query(recordQueryOf(objectId, options, cursor), {
    archivedOnly: options.archivedOnly === true,
    withTotalCount: options.withTotalCount === true,
  });
  for (const record of page.records) {
    seedCanonicalRecord(queryClient, objectId, record);
  }
  return {
    ids: page.records.map((record) => record.id),
    ...(page.nextCursor !== undefined ? { nextCursor: page.nextCursor } : {}),
    ...(page.totalCount !== undefined ? { totalCount: page.totalCount } : {}),
  };
}

/**
 * THE IDS OF EVERY LOADED PAGE, IN ORDER, WITH DUPLICATES REMOVED
 * (`table-completion.md` D12). The ONE way a record list's pages are flattened.
 *
 * WHY A DUPLICATE HAPPENS AT ALL. Keyset paging resumes from the last row of the page
 * before it, and an interactive walk cannot hold the repeatable-read snapshot the CSV
 * export holds across its pages ("a mutable sort column under concurrent writes lets keyset
 * paging both SKIP and DUPLICATE rows", `server/src/csv/export.ts`) - a transaction would
 * have to stay open across the think-time between two clicks of `Show more`. If the anchor
 * row's sort value moves BACKWARD between two pages, rows after it are returned again.
 *
 * THE DUPLICATE IS THE HALF A CLIENT CAN FIX, and it is the worse-looking half: the same
 * record id arriving twice is a duplicate React key, a console warning, and ONE record
 * rendered as TWO rows the reader can edit independently - which looks like two records
 * rather than like a paging artifact. The SKIP is not detectable here at all, because
 * nothing on the client knows what it did not receive; D12 and D13 declare it, and the
 * repair is the first re-walk that starts after the write commits, because a refetch
 * recomputes every page boundary rather than replaying stored cursors.
 *
 * IT RETURNS `undefined` FOR UNDEFINED DATA, NEVER `[]`. `RecordTable` derives its loading
 * state from `recordIds === undefined` and renders eight placeholder rows; a helper that
 * answered `[]` while the first page was in flight would paint an EMPTY table where the
 * skeleton belongs, and `useLoadingBlend` would never arm. Obligation 13.
 *
 * A `Set` is the dedupe and the order at once: first-seen insertion order is what a reader
 * saw before the duplicate arrived, so a row never moves because a later page repeated it.
 *
 * THREE OTHER CURSOR-PAGED FLATTENS EXIST AND THIS CANNOT SERVE THEM: `TasksPage`,
 * `dashboard/OpenItemsPanel` (both `page.tasks`) and `RecordTimeline` (`page.events`). The
 * signature is typed over `RecordListResult`, and generalising it over "things with an id"
 * would be a refactor of three unrelated modules inside a table module. They carry the same
 * hazard, declared in D12 and named as obligation 14's exemptions.
 */
export function pagedIds(
  data: { readonly pages: readonly RecordListResult[] } | undefined,
): readonly string[] | undefined {
  if (data === undefined) return undefined;
  const seen = new Set<string>();
  for (const page of data.pages) {
    for (const id of page.ids) seen.add(id);
  }
  return [...seen];
}

/**
 * Several records, each from its own canonical key. Used where a list has to be
 * rendered outside a row component (a relation picker, a board column) - the
 * cache entries are the same ones every other surface reads.
 */
export function useRecordsByIds(objectId: string, ids: readonly string[]) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useQueries({
    queries: ids.map((recordId) => ({
      queryKey: queryKeys.record(objectId, recordId),
      /**
       * THE SAME FUNCTION `useRecord` USES, AND THAT IS A CORRECTNESS REQUIREMENT
       * (P3 round 2, ledger P3-11's Effect and P3-15). These two hooks share
       * `queryKeys.record(objectId, recordId)`, and TanStack lets the LAST observer to
       * mount decide the entry's `queryFn` - so while this one passed a bare
       * `getRecord`, any screen with both hooks over one record had a refetch that
       * silently changed shape: no tombstone, and the relation labels a list query had
       * already paid for dropped on the floor. Home is such a screen since the inbox
       * reads the enrollments the attention panel opens with `useRecord`.
       */
      queryFn: () => readCanonicalRecord(client, queryClient, objectId, recordId),
      structuralSharing: shareRecord,
    })),
    combine: (results) => ({
      records: results
        .map((result) => result.data)
        .filter((record): record is RecordRow => record !== undefined && record !== null),
      isPending: results.some((result) => result.isPending),
      /* A read that failed, which a page must not show as a missing record (DESIGN.md H7): a
         record that does not exist answers `null`, never an error (`recordsClient.getRecord`).
         Only a read with nothing to show: a record that had answered keeps its row when a later
         read fails ("keep loaded rows on a load error"), and that is `isRefetchError`. */
      isError: results.some((result) => result.isError && result.data === undefined),
      isRefetchError: results.some((result) => result.isError && result.data !== undefined),
    }),
  });
}

/**
 * A LIST'S ROWS FROM THE CANONICAL ENTRIES, WITH ONE CACHE SUBSCRIPTION FOR THE WHOLE LIST
 * (BR-PERF; handbook I1 and I13; INV-13).
 *
 * `useRecordsByIds` mounts one observer per id, and each observer refetches its entry when it
 * mounts on a stale one. Measured on 4 Oct on the live-size fixture: going back to the Clients
 * list after 35 s made 1066 calls, 1059 of them single-record reads (`.gate/speed.json`,
 * ledger GX-SPEED-3). Here the list query is the only thing that fetches: the rows are read
 * from the canonical keys the list seeded (INV-13), and ONE subscription to the query cache
 * re-reads them when an entry of this object gets new data - a list seed, an optimistic write,
 * a rollback. No entry is observed, so a change notice (`recordInvalidations`, `refetch:
 * 'active'` on `['record', objectId]`) refetches the list and no row.
 *
 * WHAT AN OBSERVER WOULD HOLD, IT HOLDS. An entry nobody observes is collected `gcTime` after it
 * was created (query-core `Removable.scheduleGc`: five minutes here, zero in the component
 * tests), and a later seed does not restart that clock. So while it is mounted the hook keeps
 * the last data it saw for each entry of the object, as an observer keeps its last result, and
 * a row never empties because the cache let its entry go. An id with no data anywhere - an id
 * list that outlived its entries while nobody watched - counts as `missing`: the list is
 * fetched again once (`refill`), and the rows report pending until that answer is in.
 *
 * STABLE REFERENCES. Each row is the entry's own object, which structural sharing keeps when a
 * refetch brings the same values, and the array is the previous one while every row is: a
 * response re-renders only what it changed.
 */
export interface ListedRecords {
  readonly records: readonly RecordRow[];
  /** True while a listed id has no data and the one refetch for it has not answered. */
  readonly isPending: boolean;
}

/** The list query whose ids these are, to fetch it again when its entries are gone. */
export interface ListRefill {
  readonly isFetching: boolean;
  readonly refetch: () => Promise<unknown>;
}

const NO_RECORDS: readonly RecordRow[] = [];

export function useListedRecords(
  objectId: string,
  ids: readonly string[],
  refill: ListRefill,
): ListedRecords {
  const queryClient = useQueryClient();
  const store = useMemo(() => listedStore(queryClient, objectId), [queryClient, objectId]);
  const read = useSyncExternalStore(store.subscribe, () => store.read(ids));
  const refilled = useRef(false);
  const missing = read.missing > 0;
  /* Read only when something is missing: a query result tracks the properties a render
     reads, and `isFetching` would otherwise re-render the page twice per list refetch. */
  const fetching = missing && refill.isFetching;
  const { refetch } = refill;
  useEffect(() => {
    if (!missing || fetching || refilled.current) return;
    refilled.current = true;
    void refetch();
  }, [missing, fetching, refetch]);
  return useMemo(
    () => ({ records: read.records, isPending: missing && (fetching || !refilled.current) }),
    [read.records, missing, fetching],
  );
}

interface ListedRead {
  readonly records: readonly RecordRow[];
  readonly missing: number;
}

/**
 * The store behind `useListedRecords`, one per object and hook. Cache events of the object's
 * canonical entries are counted and coalesced into one notice per microtask, because a list
 * page seeds its rows in one synchronous loop: three hundred events, one re-read.
 */
function listedStore(queryClient: QueryClient, objectId: string) {
  const held = new Map<string, RecordRow>();
  let version = 0;
  let readIds: readonly string[] | null = null;
  let readVersion = -1;
  let last: ListedRead = { records: NO_RECORDS, missing: 0 };

  const subscribe = (onChange: () => void) => {
    let scheduled = false;
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.action.type !== 'success' && event.action.type !== 'setState') return;
      const [scope, object, recordId] = event.query.queryKey;
      if (scope !== 'record' || object !== objectId || typeof recordId !== 'string') return;
      const data = event.query.state.data as RecordRow | null | undefined;
      if (data === undefined || data === null) held.delete(recordId);
      else held.set(recordId, data);
      version += 1;
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        onChange();
      });
    });
  };

  const read = (ids: readonly string[]): ListedRead => {
    if (ids === readIds && version === readVersion) return last;
    readIds = ids;
    readVersion = version;
    const records: RecordRow[] = [];
    let missing = 0;
    for (const id of ids) {
      const cached = queryClient.getQueryData<RecordRow | null>(queryKeys.record(objectId, id));
      // `null` is a read that found no such record: not a row, and nothing to fetch again.
      if (cached === null) continue;
      if (cached !== undefined) held.set(id, cached);
      const row = cached ?? held.get(id);
      if (row === undefined) missing += 1;
      else records.push(row);
    }
    const same =
      missing === last.missing &&
      records.length === last.records.length &&
      records.every((row, index) => row === last.records[index]);
    if (!same) last = { records: records.length === 0 ? NO_RECORDS : records, missing };
    return last;
  };

  return { subscribe, read };
}

/**
 * One record from the canonical key. After a list query seeded the cache this
 * resolves without a request; on a deep link it fetches.
 *
 * IT ASKS FOR TOMBSTONES (`includeDeleted`), always, and the key does not change with it:
 * the canonical entry is "the row under this id", and an archived row is still that row.
 * Two consequences, both wanted (admin-area.md D21):
 *
 *  - AN ARCHIVED RECORD LOADS instead of 404ing into "this record does not exist". Before
 *    this the record page could not render a tombstone at all, so there was nothing for a
 *    banner to explain and no exit to offer.
 *  - A MERGED-AWAY ID ANSWERS AS ITSELF. `includeDeleted` suppresses the server's forward
 *    resolution (`records/read.ts`), so a stale link to a merge loser now shows the loser
 *    and its merge history. Without it the server resolves forward, the winner's values
 *    come back under the loser's URL, and `mergedInto` - the payload field that exists to
 *    disclose exactly that substitution - is not on the wire type, so nothing could tell
 *    the reader they are looking at a different person.
 */
export function useRecord(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.record(objectId, recordId),
    queryFn: () => readCanonicalRecord(client, queryClient, objectId, recordId),
    structuralSharing: shareRecord,
  });
}

/**
 * THE ONE READ BEHIND `queryKeys.record`, shared by both hooks that observe it.
 *
 * D3, and the reason this is not a plain one-liner.
 *
 * A LIST query resolves every relation label in SQL and seeds this entry with them; the
 * SINGLE-RECORD read returns bare ids and cannot. So a refetch of this key would DROP
 * labels a query had already paid for, and every relation on the screen would fall back
 * to one request per distinct target - the exact N+1 the label preservation exists to
 * remove, reintroduced by a background refresh.
 *
 * Carried forward per FIELD and only while the underlying value is UNCHANGED, so a
 * relation that was repointed since the list ran never renders the previous target's
 * name. A label whose value moved is simply dropped and re-resolved.
 *
 * IT ASKS FOR TOMBSTONES (`includeDeleted`), always, and the key does not change with it:
 * the canonical entry is "the row under this id", and an archived row is still that row.
 *
 * AND IT MERGES (`mergeRecordAnswer`): the answer is marked a full read, so a page that needs
 * every field can tell an entry this read filled from one only a list seeded
 * (`isFullRecord`).
 */
async function readCanonicalRecord(
  client: AppClient,
  queryClient: QueryClient,
  objectId: string,
  recordId: string,
): Promise<RecordRow | null> {
  const key = queryKeys.record(objectId, recordId);
  const record = await client.records.getRecord(objectId, recordId, { includeDeleted: true });
  if (record === null) return null;
  // The entry as it is when the answer lands, not when the read started: a list seed or an
  // optimistic write in between is newer than nothing this read could know about.
  const merged = mergeRecordAnswer(queryClient.getQueryData<RecordRow>(key), record);
  fullRecords.add(merged);
  return merged;
}

/**
 * ENTRIES A FULL SINGLE-RECORD READ FILLED (BR-PERF, handbook I13). A list query seeds the
 * canonical entry with the fields it selected only, so an entry can be fresh and still lack the
 * values a record page reads; `useFullRecord` re-reads exactly the entries that are not in this
 * set. A seed MERGES (`seedCanonicalRecord`), so a full entry stays full and keeps its mark; an
 * optimistic write makes a new object without it, which is what makes the page read again
 * after a write. A `WeakSet`, so a row the cache drops is dropped here too.
 */
const fullRecords = new WeakSet<RecordRow>();

/** Whether the entry carries every field a single-record read returns. */
export function isFullRecord(row: RecordRow | null | undefined): boolean {
  return row !== null && row !== undefined && fullRecords.has(row);
}

/**
 * THE CANONICAL ENTRY'S STRUCTURAL SHARING, carrying the full-read mark across it. The cache
 * stores `replaceEqualDeep(previous, answer)` (query-core `replaceData`), which is a new
 * object whenever a value moved, so a mark on the read's own answer would be lost on the way
 * in. The sharing itself is the library's, unchanged: equal values keep their objects.
 */
function shareRecord(previous: unknown, next: unknown): unknown {
  const shared = replaceEqualDeep(previous, next);
  if (isFullRecord(next as RecordRow | null) && shared !== null && typeof shared === 'object') {
    fullRecords.add(shared as RecordRow);
  }
  return shared;
}

/**
 * A SINGLE-RECORD ANSWER MERGES INTO THE ENTRY, IT NEVER REPLACES IT (BR-PERF; the 4 Oct
 * measurement, `docs/rules/decisions/CRM-URSACHENANALYSE-2026-10-04.md` section 1a).
 *
 * `GET /api/records/:objectId/:recordId` and the write answers carry the object's writable
 * user fields only (`server/src/records/shape.ts`, `recordShape`; the fixture copies it in
 * `fixtures/server-shape.ts`). A list query can carry more: a junction's two endpoints are
 * SYSTEM fields that only a `select` asks for. Replacing the entry with the narrower answer
 * dropped them, so every person beside a client lost its agency and its name until a list
 * query wrote them back - the names that vanished from the Clients list on a stale return.
 *
 * So every key the answer carries wins, null included (the server sends a key for every field
 * it carries, so a cleared value arrives as null and clears), and a key it does not carry
 * keeps the entry's value: the same rule `seedCanonicalRecord` applies to a list seed. The
 * relation labels follow `withCarriedRelationLabels` against the MERGED values, so the label of
 * an endpoint the answer did not carry stays with the value that stayed.
 */
export function mergeRecordAnswer(previous: RecordRow | undefined, next: RecordRow): RecordRow {
  if (previous === undefined) return next;
  return withCarriedRelationLabels(previous, {
    ...next,
    values: { ...previous.values, ...next.values },
  });
}

function withCarriedRelationLabels(previous: RecordRow | undefined, next: RecordRow): RecordRow {
  if (next.relationLabels !== undefined) return next;
  const carried = previous?.relationLabels;
  if (carried === undefined || previous === undefined) return next;

  const kept: Record<string, string> = {};
  for (const [fieldId, label] of Object.entries(carried)) {
    if (previous.values[fieldId] === next.values[fieldId]) kept[fieldId] = label;
  }
  return Object.keys(kept).length === 0 ? next : { ...next, relationLabels: kept };
}

/**
 * The NEWEST PAGE of a record's timeline (notes-tasks-comments.md 3.3).
 *
 * One page, no infinite scroll yet - but the page's `nextCursor` is in the cache entry, so
 * the "load more" this owes is a component that calls the client again with it, not a
 * change to the contract or to this key.
 */
/**
 * The timeline, PAGE BY PAGE (notes-tasks-comments.md 3.3).
 *
 * `useInfiniteQuery`, not `useQuery`, and the difference is not ergonomic: the endpoint
 * stopped returning every event, so a single-page hook would have silently turned "the
 * record's history" into "its newest fifty" - a regression that looks like nothing on a
 * young record and hides years on an old one. The design says the UI "loads newest page
 * first and pages back", which is this hook plus one button.
 *
 * Invalidation is unchanged: the key is the same, and TanStack refetches every loaded
 * page, so an SSE notice still brings the whole open timeline up to date.
 */
export function useRecordEvents(objectId: string, recordId: string) {
  const client = useAppClient();
  return useInfiniteQuery({
    queryKey: queryKeys.recordEvents(objectId, recordId),
    queryFn: ({ pageParam }) =>
      client.records.listEvents(objectId, recordId, {
        limit: RECORD_EVENT_PAGE_SIZE,
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    /** Null means the beginning of the history; `undefined` is what stops the pager. */
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export interface UpdateRecordVariables {
  readonly recordId: string;
  readonly values: Readonly<Record<string, FieldValue>>;
}

/**
 * Optimistic update of the canonical key, rolled back on failure. The mutation
 * RESULT is returned unchanged so the caller can react to the three write
 * outcomes (applied / pending_approval / write_blocked) - a hook must not
 * flatten "queued for approval" into "saved".
 */
export function useUpdateRecord(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recordId, values }: UpdateRecordVariables) =>
      client.records.updateRecord(objectId, recordId, values),

    onMutate: async ({ recordId, values }) => {
      const key = queryKeys.record(objectId, recordId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RecordRow>(key);
      if (previous !== undefined) {
        queryClient.setQueryData<RecordRow>(key, {
          ...previous,
          values: { ...previous.values, ...values },
        });
      }
      return { previous };
    },

    onError: (_error, { recordId }, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.record(objectId, recordId), context.previous);
      }
    },

    onSuccess: (result, { recordId }, context) => {
      const key = queryKeys.record(objectId, recordId);
      if (result.status === 'pending_approval') {
        // A successful CALL that changed nothing. The optimistic value must be
        // undone here, or every surface reading the canonical key would keep
        // showing a change nobody approved - which is precisely the lie the
        // three-outcome WriteResult exists to prevent (INV-10).
        if (context.previous !== undefined) {
          queryClient.setQueryData(key, context.previous);
        }
      } else {
        // `applied` and `write_blocked` both return the record the server KEPT,
        // so writing it covers the refused case too. MERGED, like a single read: the
        // answer has the single read's shape and would drop what a list query added. Into
        // the entry as it was BEFORE the optimistic write, whose labels still belong to its
        // values; the optimistic copy carries the old label beside a changed relation.
        queryClient.setQueryData<RecordRow>(key, (current) =>
          mergeRecordAnswer(context.previous ?? current, result.record),
        );
        /**
         * INV-6 at the optimistic call site: an edited record may have left (or joined) a
         * FILTERED list, and only the shared evaluator can say which.
         */
        reconcileFilteredLists(queryClient, objectId, result.record);
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.recordEvents(objectId, recordId) });
    },
  });
}

export function useCreateRecord(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Readonly<Record<string, FieldValue>>) =>
      client.records.createRecord(objectId, values),
    onSuccess: (result) => {
      if (result.status !== 'pending_approval') {
        queryClient.setQueryData(queryKeys.record(objectId, result.record.id), result.record);
      }
      // The new row's position depends on the sort, so the id array is refetched - and so
      // is every grouped count of this object, which a new row moved by one (D25).
      invalidateRecordReads(queryClient, objectId);
    },
  });
}

/**
 * SOFT delete (D21). The canonical entry is INVALIDATED, not removed: the record still
 * exists, it just moved to the archive, and dropping the entry would make an open record
 * page fall back to "this record does not exist" instead of to its tombstone banner.
 *
 * Every list of this object is refetched - the live one loses the row, the archived one
 * gains it - because `['recordList', objectId]` is a prefix over both segments.
 */
export function useDeleteRecord(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recordId: string) => client.records.deleteRecord(objectId, recordId),
    onSuccess: (_result, recordId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.record(objectId, recordId) });
      invalidateRecordReads(queryClient, objectId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recordEvents(objectId, recordId) });
    },
  });
}

/**
 * THE UNDO (D21), member-accessible like the delete it reverses.
 *
 * Deliberately not optimistic. The server REFUSES on a merge loser (`flipDeletion`), and
 * an optimistic un-dimming would show the row as live for the moment it takes that refusal
 * to arrive - on the one operation whose failure is the interesting case. The timeline is
 * invalidated too: a restore emits a `restored` event, and the record page shows it.
 */
export function useRestoreRecord(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recordId: string) => client.records.restoreRecord(objectId, recordId),
    onSuccess: (_result, recordId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.record(objectId, recordId) });
      invalidateRecordReads(queryClient, objectId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recordEvents(objectId, recordId) });
    },
  });
}

/**
 * D2: which related sections this object's record page renders, and in which order.
 *
 * `staleTime: Infinity` like the other metadata reads - it is a projection of the
 * metadata graph, and the same invalidation that refreshes objects and fields covers
 * it (`invalidateModel`).
 */
export function useInboundRelations(objectId: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.inboundRelations(objectId),
    queryFn: () => client.metadata.inboundRelations(objectId),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: enabled && objectId !== '',
  });
}

/**
 * D27's ANCHOR SET, with `useInboundRelations`' cache policy for its reason: it is
 * metadata, it changes only when an admin changes the model, and both of its consumers
 * mount and unmount many times per session.
 *
 * `undefined` while it is in flight is LOAD-BEARING and not a loading state to paper
 * over: `pruneFilter` KEEPS a related node when the set is unknown, because dropping on
 * "not known yet" would widen a list silently, which is the direction this whole module
 * exists to avoid.
 */
export function useFilterAnchors(objectId: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.filterAnchors(objectId),
    queryFn: () => client.metadata.filterAnchors(objectId),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: enabled && objectId !== '',
  });
}

/**
 * ADDING AN N:N MEMBERSHIP (D5). NOT optimistic, and three things make that the honest
 * choice rather than a shortcut:
 *
 *  - the row the section renders is the SERVER's junction record, endpoints and all,
 *    and the browser cannot mint its id;
 *  - a duplicate answers 409 and a gated writer answers 403, both of which the caller
 *    has to surface rather than un-render;
 *  - `restored` decides the sentence the user reads ("is a member again" vs "was
 *    added"), and only the response knows which happened.
 */
export function useLinkJunction(parentObjectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: JunctionLinkInput) => client.records.linkJunction(input),
    onSuccess: (result) => {
      queryClient.setQueryData(
        queryKeys.record(result.junctionObjectId, result.record.id),
        result.record,
      );
      // The membership list, and the PARENT's timeline: a link emits an ordinary
      // `created` event on the junction record, and the section's list is what moved.
      invalidateRecordReads(queryClient, result.junctionObjectId);
      void queryClient.invalidateQueries({ queryKey: ['recordEvents', parentObjectId] });
    },
  });
}

export function useSearchRecords(term: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.search(term),
    queryFn: () => client.records.search(term),
    enabled: term.trim().length > 1,
    staleTime: 10_000,
  });
}

// --- pending changes (INV-10) -----------------------------------------------

/** The approval queue. `pending` by default: that is the list somebody has to work. */
export function usePendingChanges(status: PendingChangeSummary['status'] = 'pending') {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.pendingChanges(status),
    queryFn: () => client.pending.list({ status }),
  });
}

/**
 * The nav badge. Polling-free: the SSE `pending` notice invalidates it, and
 * `staleTime` keeps a screen change from refetching a number that cannot have moved
 * without a notice.
 */
export function usePendingCount() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.pendingCount(),
    queryFn: () => client.pending.count(),
    staleTime: 30_000,
  });
}

export function usePendingChange(id: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.pendingChange(id),
    queryFn: () => client.pending.get(id),
    enabled,
  });
}

/**
 * Approve, or edit-then-approve. Deliberately NOT optimistic: an approval can be
 * partially refused by a provenance lock (INV-5) and the record it produces comes back
 * from the server, so guessing the outcome first would mean showing a value that may
 * never have landed - the exact lie the three-outcome write model exists to prevent.
 */
export function useApprovePendingChange() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      values,
    }: {
      readonly id: string;
      readonly values?: Readonly<Record<string, FieldValue>>;
    }) => client.pending.approve(id, values),
    onSuccess: (outcome) => {
      if (outcome.record !== null) {
        queryClient.setQueryData(
          queryKeys.record(outcome.pendingChange.objectId, outcome.record.id),
          outcome.record,
        );
      }
      invalidateAfterDecision(queryClient, outcome.pendingChange.objectId);
    },
  });
}

export function useRejectPendingChange() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { readonly id: string; readonly reason: string }) =>
      client.pending.reject(id, reason),
    onSuccess: (change) => invalidateAfterDecision(queryClient, change.objectId),
  });
}

/**
 * A decision changes the queue, the badge, and - for an approval - the record and its
 * timeline. Done here rather than left to the SSE notice, because the person who clicked
 * must see the result of their own click without waiting for a round trip through
 * PostgreSQL.
 */
function invalidateAfterDecision(queryClient: QueryClient, objectId: string): void {
  void queryClient.invalidateQueries({ queryKey: ['pendingChanges'] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.pendingCount() });
  invalidateRecordReads(queryClient, objectId);
  void queryClient.invalidateQueries({ queryKey: ['recordEvents', objectId] });
}

/**
 * D5: the plan and its cost, fetched BEFORE the drop dialog can offer a button.
 *
 * `staleTime: 0` and no retention: a preview is a statement about the database RIGHT
 * NOW, and a cached one is exactly the stale number D6 exists to refuse. Enabled by
 * the caller, so nothing is dry-run until a dialog is actually open.
 */
export function useDropPreview(target: DropTarget, enabled: boolean) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.dropPreview(target),
    queryFn: () => client.metadata.previewDrop(target),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

// --- metadata mutations -----------------------------------------------------

/**
 * Every visibility of the model this change could have touched (D8).
 *
 * Prefix keys, so the admin entry and the app entry are refreshed together. A mutation
 * that refreshed only its own caller is how an archived field survives on a record page
 * until the next reload.
 */
function invalidateModel(queryClient: QueryClient, objectId?: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeyPrefixes.objects() });
  /**
   * D2's section set, EVERY object's. A relation field belongs to the CHILD, so
   * creating, archiving or hiding one changes which sections the PARENT renders - an
   * object this call has no id for. Scoping this to `objectId` is how a new "Payments"
   * section fails to appear on the agency page until a reload.
   */
  void queryClient.invalidateQueries({ queryKey: ['inboundRelations'] });
  if (objectId === undefined) return;
  void queryClient.invalidateQueries({ queryKey: queryKeyPrefixes.object(objectId) });
  void queryClient.invalidateQueries({ queryKey: queryKeyPrefixes.fields(objectId) });
}

export function useCreateObject() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateObjectInput) => client.metadata.createObject(input),
    onSuccess: (object) => {
      queryClient.setQueryData(queryKeys.object(object.id), object);
      invalidateModel(queryClient, object.id);
    },
  });
}

export function useUpdateObject() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ objectId, input }: { objectId: string; input: UpdateObjectInput }) =>
      client.metadata.updateObject(objectId, input),
    onSuccess: (object) => {
      /**
       * The ADMIN entry, not the app's. The client reads the saved row back at the `all`
       * visibility so an admin who just HID an object still gets it - and writing that
       * row into the app's cache entry would be the visibility mode leaking: a screen
       * that may not see hidden objects would hold one until the invalidation below
       * refetched it.
       */
      queryClient.setQueryData(queryKeys.object(object.id, 'all'), object);
      invalidateModel(queryClient, object.id);
    },
  });
}

export function useCreateField(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFieldInput) => client.metadata.createField(input),
    onSuccess: () => invalidateModel(queryClient, objectId),
  });
}

export function useUpdateField(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, input }: { fieldId: string; input: UpdateFieldInput }) =>
      client.metadata.updateField(fieldId, input),
    onSuccess: () => invalidateModel(queryClient, objectId),
  });
}

/**
 * D12: the record title, written where the rule actually reads it.
 *
 * `fieldId: null` gives the object back to the rule's conventions (a TEXT field named
 * `name`, then the first TEXT field). The object's cache entry is invalidated too, not
 * just the field list: `labelIdentifierFieldId` is DERIVED from the fields, so an object
 * that kept its old value would title every record with the previous field.
 */
export function useSetLabelField(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string | null) => client.metadata.setLabelField(objectId, fieldId),
    onSuccess: () => invalidateModel(queryClient, objectId),
  });
}

/**
 * D9 archive/restore, the REVERSIBLE half of deletion and therefore the easy action.
 *
 * One hook per direction rather than a boolean parameter: the two are different
 * sentences in the UI, and a call site reading `archive.mutate()` says what it does.
 */
export function useArchiveObject() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (objectId: string) => client.metadata.archiveObject(objectId),
    onSuccess: (_result, objectId) => invalidateModel(queryClient, objectId),
  });
}

export function useRestoreObject() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (objectId: string) => client.metadata.restoreObject(objectId),
    onSuccess: (_result, objectId) => invalidateModel(queryClient, objectId),
  });
}

export function useArchiveField(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => client.metadata.archiveField(fieldId),
    onSuccess: () => invalidateModel(queryClient, objectId),
  });
}

export function useRestoreField(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => client.metadata.restoreField(fieldId),
    onSuccess: () => invalidateModel(queryClient, objectId),
  });
}

/**
 * Destructive (INV-8, D5, D6): three things travel with a drop and none of them is
 * implied - the api name a human typed, the token of the plan they were shown, and the
 * row counts that plan displayed.
 */
export interface DropRequest extends DestructiveApplyOptions {
  readonly confirmToken: string;
}

export function useDeleteField(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, confirmToken, ...options }: DropRequest & { fieldId: string }) =>
      client.metadata.deleteField(fieldId, confirmToken, options),
    onSuccess: () => invalidateModel(queryClient, objectId),
  });
}

/**
 * The object drop, which had no hook and no caller at all until now (section 1.2): the
 * client method existed, was tested, and no human could reach it.
 */
export function useDeleteObject() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ objectId, confirmToken, ...options }: DropRequest & { objectId: string }) =>
      client.metadata.deleteObject(objectId, confirmToken, options),
    onSuccess: (_result, { objectId }) => invalidateModel(queryClient, objectId),
  });
}

/**
 * ERASING EVERY RECORD OF ONE OBJECT (accounts F2, D-F2.3).
 *
 * The preflight is a QUERY and not part of the mutation, because the sheet has to read the
 * counts BEFORE it can offer a button - the same two-step `useDropPreview` gives the plan
 * preview, and for the same reason: the token proves the caller was shown this set rather
 * than that they guessed a value off a refusal.
 *
 * `staleTime: 0` and no cache sharing between openings: what this counts is a table whose
 * size is exactly what somebody else may be changing while the sheet is open.
 */
export function useEraseAllPreflight(objectId: string, enabled: boolean) {
  const client = useAppClient();
  return useQuery({
    queryKey: ['eraseAllPreflight', objectId],
    queryFn: () => client.records.eraseAllPreflight(objectId),
    staleTime: 0,
    gcTime: 0,
    enabled,
  });
}

/**
 * THE APPLY. Every read of this object is invalidated afterwards, plus the canonical record
 * entries and the timelines: nothing of this object survives, so a cached row would be a
 * record the app still believes in after the server destroyed it.
 */
export function useEraseAllRecords(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      confirmToken,
      expectedRecordCount,
    }: {
      readonly confirmToken: string;
      readonly expectedRecordCount: number;
    }) => client.records.eraseAll(objectId, confirmToken, expectedRecordCount),
    onSuccess: () => {
      invalidateRecordReads(queryClient, objectId);
      void queryClient.invalidateQueries({ queryKey: ['record', objectId] });
      void queryClient.invalidateQueries({ queryKey: ['recordEvents', objectId] });
      void queryClient.invalidateQueries({ queryKey: ['eraseAllPreflight', objectId] });
    },
  });
}

/**
 * ERASING ONE PERSON FROM THEIR OWN RECORD (wave 5, B-7; `admin-area.md` D23, the wave-4 F2
 * roadmap line, `deploy/README.md` "Erasing records").
 *
 * The pair beside the two above, and the same two-step for the same reason: the dialog has to
 * SHOW what the erasure destroys before it can offer the button, and the token proves the
 * caller was shown that exact set. What differs is the unit. This one erases a MERGE CLUSTER
 * - "after a merge, erasing the winner leaves the loser's name, address and history in an
 * ordinary row" (`data/client.ts`, `identity-resolution.md` 5.6) - so the preflight's first
 * field is the cluster and the dialog's first block is that table.
 *
 * `staleTime: 0` and no cache between openings: a merge somebody else runs while the dialog
 * is open changes the cluster, and the server answers a token bound to the old one with a
 * 409. The caller refetches on that refusal.
 */
export function useErasePreflight(objectId: string, recordId: string, enabled: boolean) {
  const client = useAppClient();
  return useQuery({
    queryKey: ['erasePreflight', objectId, recordId],
    queryFn: () => client.records.erasePreflight(objectId, recordId),
    staleTime: 0,
    gcTime: 0,
    enabled,
  });
}

/**
 * THE APPLY. Everything this object reads is invalidated, plus the canonical record entries
 * and the timelines: the cluster is gone from the server, so a cached row is a record the app
 * still believes in after it stopped existing.
 */
export function useEraseRecord(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ confirmToken }: { readonly confirmToken: string }) =>
      client.records.erase(objectId, recordId, confirmToken),
    onSuccess: () => {
      invalidateRecordReads(queryClient, objectId);
      void queryClient.invalidateQueries({ queryKey: ['record', objectId] });
      void queryClient.invalidateQueries({ queryKey: ['recordEvents', objectId] });
      void queryClient.invalidateQueries({ queryKey: ['erasePreflight', objectId, recordId] });
    },
  });
}

// --- identity resolution ----------------------------------------------------

/**
 * The namespaces, read by the field editor's picker and by the admin screen.
 *
 * `staleTime: Infinity` like the metadata queries, and for the same reason: `key`,
 * `kind` and `defaultRegion` are immutable by construction (1.3), so the only thing
 * that can change under a session is a namespace it created or renamed itself - and
 * those mutations invalidate this key.
 */
export function useIdentityNamespaces(enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.identityNamespaces(),
    queryFn: () => client.identity.listNamespaces(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled,
  });
}

export function useCreateNamespace() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNamespaceInput) => client.identity.createNamespace(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.identityNamespaces() });
    },
  });
}

export function useUpdateNamespace() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      namespaceId,
      input,
    }: {
      readonly namespaceId: string;
      readonly input: UpdateNamespaceInput;
    }) => client.identity.updateNamespace(namespaceId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.identityNamespaces() });
    },
  });
}

/**
 * Deleting a namespace. NOT optimistic: the server refuses while any live field still
 * points at it (5.2), and the refusal names those fields - which is the answer the
 * admin needs. Guessing success first would flash a row away and put it back.
 */
export function useDeleteNamespace() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (namespaceId: string) => client.identity.deleteNamespace(namespaceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.identityNamespaces() });
    },
  });
}

export function useDetectorState(enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.identityDetector(),
    queryFn: () => client.identity.getDetector(),
    enabled,
  });
}

export function useSetDetectorPaused() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paused: boolean) => client.identity.setDetector(paused),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.identityDetector(), state);
    },
  });
}

export function useRecordIdentity(objectId: string, recordId: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.identityPanel(objectId, recordId),
    queryFn: () => client.identity.getRecordIdentity(objectId, recordId),
    enabled: enabled && objectId !== '' && recordId !== '',
  });
}

export function useRecordDuplicates(objectId: string, recordId: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.recordDuplicates(objectId, recordId),
    queryFn: () => client.identity.recordDuplicates(objectId, recordId),
    enabled: enabled && objectId !== '' && recordId !== '',
  });
}

export function useObjectDuplicates(objectId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.objectDuplicates(objectId),
    queryFn: () => client.identity.objectDuplicates(objectId),
    enabled: objectId !== '',
  });
}

/**
 * What a merge would actually do. The review screen cannot name the consequences
 * before the click without this, so it is a QUERY on the pair rather than something
 * computed from the proposal payload - the payload says which values survive and
 * nothing about the linked records that move.
 */
export function useMergePreflight(
  objectId: string,
  winnerRecordId: string,
  loserRecordId: string,
  enabled = true,
) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.mergePreflight(objectId, winnerRecordId, loserRecordId),
    queryFn: () => client.identity.mergePreflight({ objectId, winnerRecordId, loserRecordId }),
    enabled: enabled && objectId !== '' && winnerRecordId !== '' && loserRecordId !== '',
  });
}

/**
 * Starting a merge from the possible-duplicates list. It creates a PROPOSAL (section 3,
 * no auto-merge at any confidence), so the queue is where it is decided and this hook
 * only has to refresh the surfaces that count it.
 */
export function useProposeMerge() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      readonly objectId: string;
      readonly recordA: string;
      readonly recordB: string;
    }) => client.identity.proposeMerge(input),
    onSuccess: (_change, input) => {
      void queryClient.invalidateQueries({ queryKey: ['pendingChanges'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.pendingCount() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.objectDuplicates(input.objectId) });
      void queryClient.invalidateQueries({ queryKey: ['recordDuplicates', input.objectId] });
    },
  });
}

export function useMerges(
  filter: { readonly objectId?: string; readonly recordId?: string },
  enabled = true,
) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.merges(filter),
    queryFn: () => client.identity.listMerges(filter),
    enabled,
  });
}

export function useUnmergePreflight(mergeId: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.unmergePreflight(mergeId),
    queryFn: () => client.identity.unmergePreflight(mergeId),
    enabled: enabled && mergeId !== '',
  });
}

/**
 * Undoing a merge (4.7). The token comes from the preflight the operator was shown, so
 * the confirmation is proof that a human saw THIS plan - the same contract the metadata
 * destructive flow has.
 *
 * Everything is invalidated rather than patched: an unmerge restores a record, gives
 * back its keys, reverts some of the winner's fields and repoints rows in other
 * objects. Reconstructing that in the cache would be a second implementation of the
 * transaction.
 */
export function useUnmerge() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mergeId,
      confirmToken,
    }: {
      readonly mergeId: string;
      readonly confirmToken?: string | undefined;
    }) => client.identity.unmerge(mergeId, confirmToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['merges'] });
      void queryClient.invalidateQueries({ queryKey: ['record'] });
      // Whole-app, with no object id: an unmerge moves rows in an object this call site
      // cannot name, which is the case `recordReadKeys()` takes no argument for.
      invalidateRecordReads(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['recordEvents'] });
      void queryClient.invalidateQueries({ queryKey: ['identityPanel'] });
      void queryClient.invalidateQueries({ queryKey: ['recordDuplicates'] });
      void queryClient.invalidateQueries({ queryKey: ['objectDuplicates'] });
    },
  });
}

// --- outbound webhooks (webhooks.md section 7) -------------------------------

/**
 * The subscription list, WITH its 24 h health. One query rather than two, because the
 * health is what turns the list from a catalogue into a status board and a screen that
 * rendered the names first and the health a moment later would flash "all fine".
 */
export function useWebhooks() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.webhooks(),
    queryFn: () => client.webhooks.list(),
    // Deliveries move on their own, so this is not metadata: a stale board is a board
    // that says an endpoint is healthy after it stopped being one.
    staleTime: 15_000,
  });
}

export function useWebhook(id: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.webhook(id),
    queryFn: () => client.webhooks.get(id),
    staleTime: 15_000,
  });
}

export function useWebhookDeliveries(id: string, status?: WebhookDeliveryStatus) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.webhookDeliveries(id, status),
    queryFn: () =>
      client.webhooks.deliveries(id, { ...(status === undefined ? {} : { status }), limit: 50 }),
    staleTime: 10_000,
  });
}

export function useCreateWebhook() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebhookSubscriptionRequest) => client.webhooks.create(input),
    onSuccess: (subscription) => {
      queryClient.setQueryData(queryKeys.webhook(subscription.id), subscription);
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks() });
    },
  });
}

export function useUpdateWebhook(id: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWebhookSubscriptionRequest) => client.webhooks.update(id, input),
    onSuccess: (subscription) => {
      queryClient.setQueryData(queryKeys.webhook(id), subscription);
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks() });
    },
  });
}

export function useDeleteWebhook() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.webhooks.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks() });
    },
  });
}

/** Returns the new secret, shown once. The caller must not cache it anywhere. */
export function useRotateWebhookSecret(id: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.webhooks.rotateSecret(id),
    onSuccess: (subscription) => {
      // Written WITHOUT the secret: the canonical cache entry must not hold a signing key
      // that every later reader of this query would receive.
      const { secret: _secret, ...rest } = subscription;
      queryClient.setQueryData(queryKeys.webhook(id), rest);
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks() });
    },
  });
}

/** Session-gated on the server; an API-key admin gets a 403 the screen surfaces. */
export function useRevealWebhookSecret(id: string) {
  const client = useAppClient();
  return useMutation({ mutationFn: () => client.webhooks.get(id, true) });
}

export function useTestWebhook(id: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.webhooks.test(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webhookDeliveries', id] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks() });
    },
  });
}

export function useReplayDelivery(webhookId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deliveryId, mode }: { deliveryId: string; mode: WebhookReplayMode }) =>
      client.webhooks.replay(deliveryId, mode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webhookDeliveries', webhookId] });
    },
  });
}

// --- notes, tasks and comments (notes-tasks-comments.md 3.5) -----------------

/**
 * The three per-record list keys, so the invalidation helper below can name a kind rather
 * than assemble a key literal. INV-13's rule is that `queryKeys.ts` is the only place a key
 * is built; a lookup table of its own functions keeps that true where a `[kind, ...]`
 * literal would have quietly forked it.
 */
const ANNOTATION_LIST_KEY = {
  notes: queryKeys.notes,
  tasks: queryKeys.tasks,
  comments: queryKeys.comments,
} as const;

/**
 * What one annotation write invalidates.
 *
 * The list itself, obviously. But ALSO the parent record's timeline, because 3.1 makes
 * every annotation mutation emit an event on the parent inside the same transaction - a
 * note that appeared while the Activity feed still claimed nothing had happened would make
 * the timeline look broken rather than stale.
 *
 * Tasks additionally reach two GLOBAL surfaces. A task belongs to a record and to a person,
 * and the my-tasks feed and its badge are keyed by neither the object nor the record, so
 * the whole prefix is the narrowest honest scope: reassigning a task on one customer
 * changes what a different user sees on `/tasks`.
 */
function invalidateAnnotation(
  queryClient: QueryClient,
  kind: keyof typeof ANNOTATION_LIST_KEY,
  objectId: string,
  recordId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: ANNOTATION_LIST_KEY[kind](objectId, recordId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.recordEvents(objectId, recordId) });
  if (kind === 'tasks') {
    void queryClient.invalidateQueries({ queryKey: ['myTasks'] });
    void queryClient.invalidateQueries({ queryKey: ['myTaskCount'] });
  }
}

export function useNotes(objectId: string, recordId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.notes(objectId, recordId),
    queryFn: () => client.annotations.listNotes(objectId, recordId),
    enabled: objectId !== '' && recordId !== '',
  });
}

export function useTasks(objectId: string, recordId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.tasks(objectId, recordId),
    queryFn: () => client.annotations.listTasks(objectId, recordId),
    enabled: objectId !== '' && recordId !== '',
  });
}

/**
 * The record's whole discussion, note threads included. The caller partitions by `noteId`
 * to hang a thread under its note card (3.5); the server would happily narrow, but a
 * request per note card is a request per scroll.
 */
export function useComments(objectId: string, recordId: string) {
  const client = useAppClient();
  /**
   * Typed as `ThreadComment[]` because the optimistic writer puts a `pending` row in this
   * cache. The server never sends the flag, so the widening is one-directional and the
   * thread reads one list rather than merging two.
   */
  return useQuery<readonly ThreadComment[]>({
    queryKey: queryKeys.comments(objectId, recordId),
    queryFn: () => client.annotations.listComments(objectId, recordId),
    enabled: objectId !== '' && recordId !== '',
  });
}

/** The global feed (12.2). One page; `nextCursor` rides in the cache entry for the rest. */
/**
 * The my-tasks feed, PAGE BY PAGE, and here paging is a correctness requirement rather
 * than a nicety.
 *
 * D9's visibility filter runs AFTER the database page is read, so a page can legitimately
 * come back with zero entries and a non-null cursor - every row on it belonged to
 * somebody else, on an object this caller may not read. A single-page hook would render
 * that as "Nothing on your plate" while the caller's own tasks sat on page two.
 */
export function useMyTasks(filter: MyTasksFilter) {
  const client = useAppClient();
  return useInfiniteQuery({
    queryKey: queryKeys.myTasks(filter),
    queryFn: ({ pageParam }) =>
      client.annotations.listMyTasks({
        ...filter,
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/**
 * HOW MANY OPEN TASKS. No argument is the tab-bar badge; a filter is a dashboard tile,
 * which asks the SAME route with the SAME parameters its panel pages through (D15).
 *
 * `staleTime` for the same reason `usePendingCount` has one: the SSE notice invalidates
 * it, so a navigation must not refetch a number that cannot have moved without one. The
 * key carries the whole filter, so the badge and the tile are separate cache entries and
 * one prefix invalidation still refreshes both.
 */
export function useMyTaskCount(filter?: MyTaskCountFilter, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.myTaskCount(filter),
    queryFn: () => client.annotations.countMyOpenTasks(filter),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * THE CALLER'S OWN PREFERENCES (dashboard.md D33).
 *
 * Held for the session: nobody else can change this row, so there is nothing to
 * re-read. `staleTime: Infinity` rather than a number for exactly that reason - the only
 * thing that can move it is the mutation below, which writes the entry itself.
 */
export function usePreferences() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.preferences(),
    queryFn: () => client.auth.getPreferences(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Upsert one preference key, OPTIMISTICALLY.
 *
 * A checkbox that waits for a round trip before it ticks is a checkbox people press
 * twice. The panel disappears on the frame the box is unticked and the write follows; a
 * failure rolls the entry back and the caller shows the error, which is the same shape
 * every other mutation in this file uses.
 */
export function useSetPreference() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { readonly key: string; readonly value: unknown }) =>
      client.auth.setPreference(key, value),
    onMutate: ({ key, value }) => {
      const previous = queryClient.getQueryData<UserPreferences>(queryKeys.preferences());
      queryClient.setQueryData<UserPreferences>(queryKeys.preferences(), {
        ...(previous ?? {}),
        [key]: value,
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.preferences(), context.previous);
      }
    },
  });
}

/**
 * The assignee picker's users. Held long: an account is created in the admin screen and
 * deactivated there, and neither happens while somebody is picking an assignee.
 */
export function useAnnotationUsers(enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.annotationUsers(),
    queryFn: () => client.annotations.listUsers(),
    staleTime: 5 * 60_000,
    enabled,
  });
}

// --- note mutations ---------------------------------------------------------

export function useCreateNote(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => client.annotations.createNote(objectId, recordId, body),
    onSuccess: () => invalidateAnnotation(queryClient, 'notes', objectId, recordId),
  });
}

export interface UpdateNoteVariables {
  readonly noteId: string;
  readonly body: string;
  /**
   * The `updated_at` the editor opened on (2.6). Absent means "I am claiming no version",
   * which is a legitimate thing for a one-line inline edit to say; present means the server
   * answers 409 with the current row instead of overwriting somebody mid-sentence.
   */
  readonly ifUnmodifiedSince?: string;
}

/**
 * Editing a note. NOT optimistic, and the lock is the reason: the call can come back 409
 * carrying somebody else's text, and a cache that had already shown the local version would
 * have to un-show it. The composer keeps the draft; the list waits for the answer.
 */
export function useUpdateNote(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, body, ifUnmodifiedSince }: UpdateNoteVariables) =>
      client.annotations.updateNote(noteId, body, ifUnmodifiedSince),
    onSuccess: () => invalidateAnnotation(queryClient, 'notes', objectId, recordId),
  });
}

export function useDeleteNote(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => client.annotations.deleteNote(noteId),
    onSuccess: () => invalidateAnnotation(queryClient, 'notes', objectId, recordId),
  });
}

// --- task mutations ---------------------------------------------------------

export function useCreateTask(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskRequest) =>
      client.annotations.createTask(objectId, recordId, input),
    onSuccess: () => invalidateAnnotation(queryClient, 'tasks', objectId, recordId),
  });
}

export interface UpdateTaskVariables {
  readonly taskId: string;
  readonly patch: UpdateTaskRequest;
}

/**
 * The optimistic half of a task edit, and it is deliberately ONLY `done`.
 *
 * The checkbox is the one task change whose outcome the client can predict exactly - it
 * flips a timestamp - and the one that has to feel instant, because a checkbox that lags
 * reads as a checkbox that did not register. Every other field carries a name the SERVER
 * resolves (`assigneeName`, `assigneeIsActive`, `doneByName`), so guessing a reassignment
 * would put the previous assignee's name under the new assignee's id and call it saved.
 *
 * `doneAt` and `doneBy` move together because the table's CHECK says they must
 * (`(done_at is null) = (done_by is null)`); a cache entry that broke that would be a
 * shape no server response can produce.
 */
function withOptimisticDone(
  task: TaskSummary,
  patch: UpdateTaskRequest,
  session: AuthenticatedUser | null,
): TaskSummary {
  if (patch.done === undefined) return task;
  if (!patch.done) return { ...task, doneAt: null, doneBy: null, doneByName: null };
  return {
    ...task,
    doneAt: new Date().toISOString(),
    doneBy: { source: 'MANUAL', ...(session === null ? {} : { userId: session.id }) },
    doneByName: session?.name ?? null,
  };
}

export function useUpdateTask(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  const key = queryKeys.tasks(objectId, recordId);

  /**
   * THE SAME TASK LIVES IN TWO CACHES, and the checkbox is tapped from both.
   *
   * The record page reads `['tasks', objectId, recordId]`; /tasks reads
   * `['myTasks', filter]`, paginated. Patching only the first meant the /tasks checkbox
   * had no optimism at all - it waited for the round trip and then for a refetch, on the
   * one screen 12.2 explicitly asks to complete "in place, optimistically". Worse, its
   * rollback targeted a key that page never read, so a failed write left the tick showing.
   *
   * `objectId` is empty when the parent is restricted (D9), which makes `key` a key
   * nothing reads - harmless, and the my-tasks patch below is what carries that row.
   */
  const patchMyTasks = (taskId: string, apply: (task: TaskSummary) => TaskSummary): void => {
    queryClient.setQueriesData<InfiniteData<MyTasksPage>>({ queryKey: ['myTasks'] }, (current) =>
      current === undefined
        ? current
        : {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              tasks: page.tasks.map((entry) =>
                entry.task.id === taskId ? { ...entry, task: apply(entry.task) } : entry,
              ),
            })),
          },
    );
  };

  return useMutation({
    mutationFn: ({ taskId, patch }: UpdateTaskVariables) =>
      client.annotations.updateTask(taskId, patch),

    onMutate: async ({ taskId, patch }) => {
      await queryClient.cancelQueries({ queryKey: key });
      await queryClient.cancelQueries({ queryKey: ['myTasks'] });
      const previous = queryClient.getQueryData<readonly TaskSummary[]>(key);
      const previousMyTasks = queryClient.getQueriesData<InfiniteData<MyTasksPage>>({
        queryKey: ['myTasks'],
      });
      const session = queryClient.getQueryData<AuthenticatedUser | null>(queryKeys.session());
      if (previous !== undefined) {
        queryClient.setQueryData<readonly TaskSummary[]>(
          key,
          previous.map((task) =>
            task.id === taskId ? withOptimisticDone(task, patch, session ?? null) : task,
          ),
        );
      }
      patchMyTasks(taskId, (task) => withOptimisticDone(task, patch, session ?? null));
      return { previous, previousMyTasks };
    },

    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(key, context.previous);
      /** Every my-tasks page is restored by its own key: the filter is part of it. */
      for (const [queryKey, data] of context?.previousMyTasks ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },

    onSuccess: (task) => {
      // The server's row replaces the guess before the refetch lands, so the row settles
      // once rather than flickering through a third state on the way.
      queryClient.setQueryData<readonly TaskSummary[]>(key, (current) =>
        current?.map((row) => (row.id === task.id ? task : row)),
      );
      patchMyTasks(task.id, () => task);
      invalidateAnnotation(queryClient, 'tasks', objectId, recordId);
    },
  });
}

export function useDeleteTask(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => client.annotations.deleteTask(taskId),
    onSuccess: () => invalidateAnnotation(queryClient, 'tasks', objectId, recordId),
  });
}

// --- comment mutations ------------------------------------------------------

/**
 * Posting a comment, optimistically (3.5). A discussion is the one surface where a
 * round-trip between pressing send and seeing the message is felt as the product being
 * slow rather than the network being slow, and the provisional row is honest: it carries
 * the session's own actor, which is exactly who the server will stamp on it.
 *
 * The id is a real UUIDv7 rather than a `temp-` marker so nothing downstream has to know
 * this row is provisional; `onSuccess` swaps it for the server's row by that id.
 */
function optimisticComment(
  objectId: string,
  recordId: string,
  input: CreateCommentRequest,
  session: AuthenticatedUser | null,
): ThreadComment {
  const now = new Date().toISOString();
  return {
    /**
     * A CLIENT-MINTED id, which is precisely why `pending` travels with it: this row
     * cannot be replied to or deleted until the server's own row replaces it below.
     */
    pending: true,
    id: newId(),
    objectId,
    recordId,
    body: input.body,
    noteId: input.noteId ?? null,
    replyToCommentId: input.replyToCommentId ?? null,
    createdBy: { source: 'MANUAL', ...(session === null ? {} : { userId: session.id }) },
    updatedBy: null,
    createdByName: session?.name ?? null,
    updatedByName: null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    humanTouchedAt: null,
  };
}

export function useCreateComment(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  const key = queryKeys.comments(objectId, recordId);

  return useMutation({
    mutationFn: (input: CreateCommentRequest) =>
      client.annotations.createComment(objectId, recordId, input),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<readonly ThreadComment[]>(key);
      const session = queryClient.getQueryData<AuthenticatedUser | null>(queryKeys.session());
      const provisional = optimisticComment(objectId, recordId, input, session ?? null);
      if (previous !== undefined) {
        // Appended: a thread reads oldest first, and the message somebody just sent belongs
        // at the bottom where they are already looking.
        queryClient.setQueryData<readonly ThreadComment[]>(key, [...previous, provisional]);
      }
      return { previous, provisionalId: provisional.id };
    },

    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(key, context.previous);
    },

    onSuccess: (comment, _input, context) => {
      /** The server's row REPLACES the claim, id and all, so `pending` disappears with it. */
      queryClient.setQueryData<readonly ThreadComment[]>(key, (current) =>
        current?.map((row) => (row.id === context.provisionalId ? comment : row)),
      );
      invalidateAnnotation(queryClient, 'comments', objectId, recordId);
    },
  });
}

export interface UpdateCommentVariables {
  readonly commentId: string;
  readonly body: string;
  /** As on notes: a version claim the server answers 409 against, or nothing. */
  readonly ifUnmodifiedSince?: string;
}

export function useUpdateComment(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, body, ifUnmodifiedSince }: UpdateCommentVariables) =>
      client.annotations.updateComment(commentId, body, ifUnmodifiedSince),
    onSuccess: () => invalidateAnnotation(queryClient, 'comments', objectId, recordId),
  });
}

/**
 * Deleting a comment. NOT optimistic even though posting one is: a delete leaves a
 * TOMBSTONE in the thread (2.4), so the row does not disappear - it changes into something
 * else - and the server decides whether the caller was allowed to delete it at all.
 */
export function useDeleteComment(objectId: string, recordId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => client.annotations.deleteComment(commentId),
    onSuccess: () => invalidateAnnotation(queryClient, 'comments', objectId, recordId),
  });
}

// --- the admin area (docs/decisions/admin-area.md) ---------------------------

/**
 * Accounts. INV-13: through `data/hooks.ts` and `queryKeys.ts` like everything else -
 * `UserAdminPage` used to call `useQuery` inline, and this module brings it into line
 * rather than copying the exception.
 */
export function useUsers() {
  const client = useAppClient();
  return useQuery({ queryKey: queryKeys.users(), queryFn: () => client.auth.listUsers() });
}

export function useCreateUser() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      readonly email: string;
      readonly password: string;
      readonly name: string;
      /** D18.1 rule 1: the whole set, and an empty one means `Coach`. */
      readonly roleIds: readonly string[];
    }) => client.auth.createUser(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

export interface UpdateUserVariables {
  readonly userId: string;
  readonly changes: {
    readonly isActive?: boolean;
    readonly name?: string;
  };
}

/**
 * Activation and display name (D14). ROLES ARE NOT HERE: D18.1 rule 4 moved assignment to
 * its own route, because the escalation ceiling has to see the whole set being assigned.
 *
 * NOT optimistic: every refusal is server-side (the last person who can administer access,
 * yourself, and the account being gone), so a row that flipped and then flipped back would
 * teach the user that the screen guesses.
 */
export function useUpdateUser() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, changes }: UpdateUserVariables) =>
      client.auth.updateUser(userId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

/**
 * The admin reset (D15). The password is in the RESULT and nowhere else, so the screen
 * reads it off the mutation rather than from a cache entry - `onSuccess` deliberately
 * writes nothing anywhere.
 */
export function useResetPassword() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => client.auth.resetPassword(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

/**
 * PENDING INVITATIONS (accounts F1). Read by the Members pane's Invitations group, which
 * renders nothing when the list is empty.
 */
export function useInvitations() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.invitations(),
    queryFn: () => client.auth.listInvitations(),
  });
}

/**
 * The accept path is in the RESULT and nowhere else, so the sheet reads it off the
 * mutation - exactly as `useResetPassword` reads the generated password. `onSuccess`
 * deliberately caches nothing: a one-time link in a query cache is a one-time link that
 * outlives the sheet that showed it.
 */
export function useCreateInvitation() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly email: string; readonly roleIds: readonly string[] }) =>
      client.auth.createInvitation(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations() });
    },
  });
}

export function useRevokeInvitation() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => client.auth.revokeInvitation(invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations() });
    },
  });
}

/**
 * The admin-issued reset link (D-F1.4), which replaced the generated password as the
 * member row's reset action: the person chooses their own, so the admin never learns it.
 */
export function useCreateResetLink() {
  const client = useAppClient();
  return useMutation({
    mutationFn: (userId: string) => client.auth.createResetLink(userId),
  });
}

export function useChangeOwnPassword() {
  const client = useAppClient();
  return useMutation({
    mutationFn: (input: { readonly currentPassword: string; readonly newPassword: string }) =>
      client.auth.changeOwnPassword(input),
  });
}

export function useApiKeys() {
  const client = useAppClient();
  return useQuery({ queryKey: queryKeys.apiKeys(), queryFn: () => client.auth.listApiKeys() });
}

/** The plaintext key is in the RESULT only; the list never carries it. */
export function useCreateApiKey() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      readonly label: string;
      /** D18 step 4: an empty list is legal and means a key that can do nothing. */
      readonly roleIds: readonly string[];
      readonly isAgent: boolean;
    }) => client.auth.createApiKey(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
    },
  });
}

export function useRevokeApiKey() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => client.auth.revokeApiKey(keyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
    },
  });
}

/**
 * The whole agent policy in ONE cache entry (D17, D19, D20): the two global switches and
 * every per-object row. Splitting them is how a screen ends up rendering "direct" beside
 * a brake that is on, because `effectiveDirectWrite` is computed by the SERVER from the
 * switch and the two halves would then be two different reads.
 */
export function useAgentPolicy() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.agentPolicy(),
    queryFn: () => client.admin.getAgentPolicy(),
  });
}

export function useSetGlobalAgentPolicy() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: {
      readonly agentWritesForcePending?: boolean;
      readonly agentAnnotationsEnabled?: boolean;
    }) => client.admin.setGlobalAgentPolicy(changes),
    /** The server answers with the whole snapshot, so the cache takes it verbatim. */
    onSuccess: (snapshot) => {
      queryClient.setQueryData(queryKeys.agentPolicy(), snapshot);
    },
  });
}

/**
 * The per-object opt-in. Invalidated rather than patched in place: the row the server
 * returns carries the effective value, but so does every OTHER row if the brake moved,
 * and one refetch is cheaper to reason about than a partial merge.
 */
export function useSetObjectAgentPolicy() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ objectId, directWrite }: { objectId: string; directWrite: boolean }) =>
      client.admin.setObjectAgentPolicy(objectId, directWrite),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentPolicy() });
    },
  });
}

export function useInstanceInfo() {
  const client = useAppClient();
  return useQuery({ queryKey: queryKeys.instance(), queryFn: () => client.admin.getInstance() });
}

/**
 * THE INSTALLATION'S NAME, for the sign-in screen's second line (login.md 2.3).
 *
 * Read ONCE and never refetched: it is a fact about the instance, set in the deployment,
 * and it cannot change while somebody looks at a sign-in form. `retry: false` for the same
 * reason the recovery mode has it (`app/auth/ForgotPasswordPage.tsx`) - an instance that
 * cannot answer this is not one a reader should watch three attempts fail on, and the
 * screen's own sentence is the safe half of the answer.
 */
export function useWorkspace() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.workspace(),
    queryFn: () => client.auth.getWorkspace(),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function useReady() {
  const client = useAppClient();
  return useQuery({ queryKey: queryKeys.ready(), queryFn: () => client.admin.getReady() });
}

/**
 * Open phase-B actions. Refetched on an interval while the card is mounted, because an
 * action moving from `applying` to `applied` or `failed` produces no notification of any
 * kind - the alternative is an operator staring at a stale list.
 */
export function useMigrationActions() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.migrationActions(),
    queryFn: () => client.admin.listMigrationActions(),
    refetchInterval: 15_000,
  });
}

export function useRetryMigrationAction() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (actionId: string) => client.admin.retryMigrationAction(actionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.migrationActions() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.instance() });
    },
  });
}

// --- saved views (record-page-and-views.md D14-D19) --------------------------

/**
 * The views of ONE object. Not `staleTime: Infinity` like the metadata reads, and the
 * difference is real: metadata changes only through a mutation this client made or a
 * live notice, while a view is a shared thing five to fifteen people edit, and a
 * switcher listing a view somebody deleted an hour ago is a switcher that 404s on click.
 */
/**
 * REPORTS (`docs/decisions/reports.md` T7).
 *
 * `staleTime` is longer than a view's: a report is read far more often than it is edited,
 * and its NUMBERS come from a separate query that has its own freshness. Splitting the two
 * is what lets the range switch refetch the numbers without re-reading the definition.
 */
export function useReports() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.reports(),
    queryFn: () => client.reports.list(),
    staleTime: 60_000,
  });
}

export function useReport(reportId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.report(reportId),
    queryFn: () => client.reports.get(reportId),
    enabled: reportId !== '',
    staleTime: 60_000,
  });
}

/**
 * THE BROWSER'S OWN ZONE, resolved once per call rather than stored (D13): a bucket key is
 * a wall-clock date and the person reading it is the person whose wall it is. A laptop that
 * crosses a timezone gets the new answer on the next read, which is the correct one.
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function useReportRun(reportId: string, range?: ReportRange) {
  const client = useAppClient();
  const timeZone = browserTimeZone();
  return useQuery({
    queryKey: queryKeys.reportRun(reportId, range ?? 'saved', timeZone),
    queryFn: () =>
      client.reports.run(reportId, { ...(range === undefined ? {} : { range }), timeZone }),
    enabled: reportId !== '',
    /**
     * D20's shape: a re-read holds the previous numbers rather than blanking the chart, so
     * the page does not flash a skeleton every time the range moves.
     */
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

/**
 * The three writes, all invalidating the report list AND the one report, and none of them
 * optimistic - the reason `useCreateView` gives applies word for word: a report write can
 * be refused for a duplicate name (409 from a partial unique index) and for every shape
 * rule `reports/service.ts` owns, and guessing success would mean re-implementing that
 * validator here.
 */
export function useCreateReport() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReportWriteInput) => client.reports.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.reports() }),
  });
}

export function useUpdateReport() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, patch }: { reportId: string; patch: ReportPatchInput }) =>
      client.reports.update(reportId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.reports() }),
  });
}

export function useDeleteReport() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) => client.reports.remove(reportId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.reports() }),
  });
}

/**
 * THE FIELDS OF SEVERAL OBJECTS AT ONCE, as one map.
 *
 * The health rule editor needs them: a rule may read a field on the scored object or on
 * anything it links to, so the picker's option list spans a set of objects whose SIZE is
 * data. A `useFields` call per object would be a hook in a loop; `useQueries` is what
 * TanStack provides for exactly that, and it keeps every object's fields under the SAME
 * cache key `useFields` uses, so nothing is fetched twice (INV-13).
 *
 * A2's mapping table asks the same question of a different set: it offers a target per field
 * of every object the mapping reaches, which is one object today and five tomorrow. The two
 * screens share this hook rather than each keeping their own, because two hooks over one
 * cache key is how a list goes stale on one screen and not the other.
 */
export function useFieldsOfObjects(objectIds: readonly string[]) {
  const client = useAppClient();
  const ids = [...new Set(objectIds.filter((id) => id !== ''))];
  return useQueries({
    queries: ids.map((objectId) => ({
      queryKey: queryKeys.fields(objectId),
      queryFn: () => client.metadata.listFields(objectId),
      staleTime: Number.POSITIVE_INFINITY,
    })),
    combine: (results) => ({
      byObject: new Map(
        ids.map((objectId, index) => [objectId, results[index]?.data ?? []] as const),
      ),
      isPending: results.some((result) => result.isPending),
    }),
  });
}

/**
 * THE HEALTH RULES (`phase-b-coach.md` D12, D17).
 *
 * ONE ENTRY, READ BY TWO SCREENS THAT MUST AGREE. The Settings page edits it and the record
 * page reads it to decide that the five fields in front of it are the computed block rather
 * than five ordinary rows. Two cache entries would let a threshold change on one screen and
 * a block disappear on the other, which is INV-13's whole argument in miniature.
 *
 * `staleTime` is a report's rather than a view's: rules are read on every record page and
 * edited a handful of times a year.
 */
export function useHealthConfigs() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.healthConfigs(),
    queryFn: () => client.health.list(),
    staleTime: 60_000,
  });
}

export function useUpdateHealthConfig() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, config }: { id: string; config: HealthConfig }) =>
      client.health.update(id, config),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.healthConfigs() }),
  });
}

/**
 * "Run now", and it invalidates the RECORDS rather than the rules.
 *
 * The pass changes no rule; it changes customers. So the thing that has to be re-read is
 * every record list and every record entry on screen - which is exactly what a live record
 * notice would do, and this is the same invalidation by hand for the one case where the
 * caller already knows the writes happened.
 */
export function useRunHealthConfig() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.health.run(id),
    onSuccess: () => {
      /**
       * `invalidateRecordReads` is the ONE helper that owns the record read prefixes
       * (obligation 41), and it takes no object id here on purpose: a pass writes to every
       * record of the scored object AND files tasks on them, and the caller is a settings
       * screen that holds no object id of its own.
       */
      invalidateRecordReads(queryClient);
      /** The canonical entries too: a record page open on a customer the pass recoloured. */
      void queryClient.invalidateQueries({ queryKey: ['record'] });
    },
  });
}

export function useViews(objectId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.views(objectId),
    queryFn: () => client.views.list(objectId),
    enabled: objectId !== '',
    staleTime: 30_000,
  });
}

/**
 * The three writes, all invalidating the SAME key and none of them optimistic.
 *
 * A view write can be refused for reasons the browser deliberately does not model: a
 * duplicate name (409 from a partial unique index), a second person taking the default
 * slot in the same instant, and every shape rule `views/validate.ts` owns. Guessing
 * success and rolling back would mean re-implementing that validator here, which is the
 * second-validator mistake INV-6 exists to prevent.
 */
export function useCreateView(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SavedViewWriteInput) => client.views.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.views(objectId) });
    },
  });
}

export function useUpdateView(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      viewId,
      patch,
    }: {
      readonly viewId: string;
      readonly patch: SavedViewPatchInput;
    }) => client.views.update(viewId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.views(objectId) });
    },
  });
}

export function useDeleteView(objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => client.views.remove(viewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.views(objectId) });
    },
  });
}

// --- access roles (roles-and-permissions.md section 6) ----------------------

/**
 * The role list, readable by ANY signed-in person (guardrail 7: DENIAL IS VISIBLE, NOT
 * HIDDEN). The Members table renders a chip per held role for everybody, and a screen that
 * could not name the roles would have to render uuids.
 */
export function useRoles() {
  const client = useAppClient();
  return useQuery({ queryKey: queryKeys.roles(), queryFn: () => client.auth.listRoles() });
}

export function useRole(roleId: string | null) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.role(roleId ?? 'none'),
    queryFn: () => client.auth.getRole(roleId as string),
    enabled: roleId !== null,
  });
}

/** Who holds a role: people AND keys, because section 6 renders one table of principals. */
export function useRoleHolders(roleId: string | null) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.roleHolders(roleId ?? 'none'),
    queryFn: () => client.auth.listRoleHolders(roleId as string),
    enabled: roleId !== null,
  });
}

/** Section 6's read-only "who can administer access", reachable by anyone. */
export function useRoleAdministrators() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.roleAdministrators(),
    queryFn: () => client.auth.listRoleAdministrators(),
  });
}

export function useCreateRole() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleRequest) => client.auth.createRole(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles() });
    },
  });
}

export function useUpdateRole() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleId,
      patch,
    }: {
      readonly roleId: string;
      readonly patch: UpdateRoleRequest;
    }) => client.auth.updateRole(roleId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
    },
  });
}

export function useDeleteRole() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => client.auth.deleteRole(roleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles() });
    },
  });
}

/**
 * NOT OPTIMISTIC, and for the same reason `useUpdateUser` is not: every refusal is
 * server-side - the escalation ceiling (guardrail 1), the last `ROLES` holder (guardrail 2)
 * and D13's key rule - so a chip that appeared and then vanished would teach the user that
 * the screen guesses at authority.
 */
export function useAssignUserRoles() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      roleIds,
    }: {
      readonly userId: string;
      readonly roleIds: readonly string[];
    }) => client.auth.assignUserRoles(userId, roleIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles() });
      /** The caller may have changed their OWN union, and the shell gates on it. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.session() });
    },
  });
}

export function useAssignApiKeyRoles() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      apiKeyId,
      roleIds,
    }: {
      readonly apiKeyId: string;
      readonly roleIds: readonly string[];
    }) => client.auth.assignApiKeyRoles(apiKeyId, roleIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles() });
    },
  });
}

// --- inbound intake (inbound-intake.md D19) ----------------------------------

/**
 * THE SOURCE LIST, and it is not metadata.
 *
 * A source's DEFINITION only changes when somebody edits it, but its `lastPullAt` and
 * `lastPullError` move on their own - a nightly pull writes both without anybody looking -
 * and the list renders exactly those. So the stale time is the webhook board's rather than
 * the metadata reads': a list that says a source is healthy after its pull started failing
 * is the same lie a delivery board tells about a dead endpoint.
 */
export function useIntakeSources() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.intakeSources(),
    queryFn: () => client.intake.list(),
    staleTime: 15_000,
  });
}

/**
 * A2: whether the module's own delivery object was ensured at boot, from the same entry the
 * list already holds. A hook rather than a field read at the call site, so the one screen
 * that shows it does not have to know which request carries it.
 */
export function useIntakeObjectFailure(): string | null {
  return useIntakeSources().data?.objectFailure ?? null;
}

export function useIntakeSource(sourceId: string) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.intakeSource(sourceId),
    queryFn: () => client.intake.get(sourceId),
    enabled: sourceId !== '',
    staleTime: 15_000,
  });
}

/**
 * The counts, per source. Its own entry rather than a field on the source, because it moves
 * on a different clock: an external system delivering something changes these four numbers
 * and nothing about the definition beside them.
 */
export function useIntakeSourceStatus(sourceId: string, enabled = true) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.intakeSourceStatus(sourceId),
    queryFn: () => client.intake.status(sourceId),
    enabled: enabled && sourceId !== '',
    staleTime: 10_000,
  });
}

/**
 * The four writes, none of them optimistic, and the reason `useCreateReport` gives applies
 * word for word here and then some: a save is REFUSED when the document does not fit live
 * metadata (`DEVIATIONS-INBOUND.md` entry 5), and guessing success would mean carrying a
 * copy of `validateSource` in the browser - the second validator INV-6 exists to prevent.
 */
export function useCreateIntakeSource() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: IntakeSourceWriteInput) => client.intake.create(input),
    onSuccess: (source) => {
      queryClient.setQueryData(queryKeys.intakeSource(source.id), source);
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources() });
    },
  });
}

export function usePatchIntakeSource(sourceId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: IntakeSourcePatchInput) => client.intake.patch(sourceId, patch),
    onSuccess: (source) => {
      queryClient.setQueryData(queryKeys.intakeSource(sourceId), source);
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources() });
    },
  });
}

export function useDeleteIntakeSource() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) => client.intake.remove(sourceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources() }),
  });
}

/**
 * THE CREDENTIAL GOES ONE WAY (P4, D13). What comes back is the DATE, so the source entry
 * is invalidated rather than written from the response: `credentialSetAt` is one field of a
 * row this mutation did not otherwise read, and storing a hand-built row here is how a
 * screen ends up rendering a source whose other fields are a guess.
 *
 * It also CLEARS A PAUSED PULL on the server (`DEVIATIONS-INBOUND.md` entry 8), which is
 * exactly why the whole source has to be re-read rather than patched in place.
 */
export function useSetIntakeCredential(sourceId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secret: string) => client.intake.setCredential(sourceId, secret),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSource(sourceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources() });
    },
  });
}

/** A question, not a change: it writes no cache entry, because it stores nothing. */
export function useValidateIntakeSource(sourceId: string) {
  const client = useAppClient();
  return useMutation({
    mutationFn: (candidate: IntakeValidateInput | undefined) =>
      client.intake.validate(sourceId, candidate),
  });
}

/** The rehearsal. Same shape as the performance, and it writes nothing either. */
export function useIntakeDryRun(sourceId: string) {
  const client = useAppClient();
  return useMutation({ mutationFn: (payload: unknown) => client.intake.dryRun(sourceId, payload) });
}

/**
 * A2: THE PROPOSAL, and it writes NOTHING - not to the server and not to the cache.
 *
 * What comes back is filled into the table the operator is already looking at, badged, and
 * saved through `usePatchIntakeSource` if they keep it. Writing it into a cache entry would
 * make a suggestion look like a stored mapping on the next render, which is exactly the
 * confusion "the AI proposes, the human confirms" exists to prevent.
 */
export function useSuggestIntakeMapping(sourceId: string) {
  const client = useAppClient();
  return useMutation({
    mutationFn: (sample: unknown) => client.intake.suggestMapping(sourceId, sample),
  });
}

/**
 * THE PLAINTEXT KEY IS NEVER CACHED. `onSuccess` invalidates the source - its `apiKeyId`
 * changed, and a second mint revoked the previous key - and deliberately writes NOTHING to
 * the cache from the result, which is the rule `useRotateWebhookSecret` states in the same
 * words: a canonical entry must not hold a credential every later reader of that query
 * would receive. The screen holds it in component state and loses it on navigation (D19).
 */
export function useMintIntakeKey(sourceId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.intake.mintKey(sourceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSource(sourceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources() });
    },
  });
}

/**
 * Pull now, which is also the RESUME: the person pressing it is the person who just fixed
 * the reason the pull paused. Both the source (its `lastPullAt` and `lastPullError`) and
 * its counts change, so both entries are invalidated.
 */
export function usePullIntakeSource(sourceId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.intake.pullNow(sourceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSource(sourceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSourceStatus(sourceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources() });
    },
  });
}

/**
 * D11's OPERATOR RETRY, on one stored delivery. Nothing redelivers a job that writes
 * customer records, so a delivery that failed for a reason somebody has since fixed needs a
 * person to say "try that again".
 *
 * It invalidates the RECORD READS as well as the counts, because the submission's own
 * status field is what the retry changes and the row rendering it is an ordinary record.
 */
export function useApplyIntakeSubmission(sourceId: string, objectId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recordId: string) => client.intake.applySubmission(recordId),
    onSuccess: () => {
      invalidateRecordReads(queryClient, objectId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.intakeSourceStatus(sourceId) });
    },
  });
}
